import React, { useCallback, useEffect, useState } from "react";
import { api, extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Tag, RefreshCw, Lock, ShieldCheck, XCircle, Undo2, Send, FlaskConical, Plus } from "lucide-react";

const STATUS_PILL = {
  draft: "bg-slate-100 text-slate-700",
  submitted: "bg-amber-100 text-amber-900",
  under_review: "bg-sky-100 text-sky-900",
  visible_to_owner: "bg-emerald-100 text-emerald-900",
  selected: "bg-emerald-200 text-emerald-900 ring-1 ring-emerald-400",
  rejected: "bg-rose-100 text-rose-900",
  cancelled: "bg-rose-100 text-rose-900",
  archived: "bg-slate-200 text-slate-600",
};

const fmtEur = (n) => {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0); }
  catch { return `€${Math.round(Number(n) || 0)}`; }
};
const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toISOString().slice(0, 10); } catch { return String(s).slice(0,10); } };

const AdminVendorOffers = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [reasonInput, setReasonInput] = useState("");
  const [acting, setActing] = useState(false);

  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({
    request_id: "", vendor_id: "", offered_price: "",
    delivery_conditions: "", cancellation_policy: "", expiration_date: "",
    response_time_hours: "", language: "es", staff_notes: "",
  });

  const fetchList = useCallback(async () => {
    try {
      const r = await api.get("/admin/offers?limit=50");
      setRows(r.data?.offers || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminOffers.toasts.failed")));
    }
  }, [t]);

  const fetchPrerequisites = useCallback(async () => {
    try {
      const [r, v] = await Promise.all([
        api.get("/admin/marketplace-requests?status=approved_internal&limit=50"),
        api.get("/admin/vendors?verified_status=internal_verified&is_active=true&limit=50"),
      ]);
      setRequests(r.data?.requests || []);
      setVendors(v.data?.vendors || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => { fetchList(); fetchPrerequisites(); }, [fetchList, fetchPrerequisites]);

  const openDrawer = async (offer) => {
    setOpenId(offer.id); setDetail(null); setAuditEntries([]); setReasonInput("");
    try {
      const [d, a] = await Promise.all([
        api.get(`/admin/offers/${offer.id}`),
        api.get(`/admin/offers/${offer.id}/audit-log`),
      ]);
      setDetail(d.data); setAuditEntries(a.data?.entries || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminOffers.toasts.failed")));
    }
  };
  const closeDrawer = () => { setOpenId(null); setDetail(null); setAuditEntries([]); setReasonInput(""); };

  const create = async () => {
    try {
      const req = requests.find((r) => r.id === form.request_id);
      if (!req) { toast.error(t("adminOffers.noRequestApproved")); return; }
      const vendor = vendors.find((v) => v.id === form.vendor_id);
      const payload = {
        request_id: form.request_id, vendor_id: form.vendor_id,
        vendor_name_snapshot: vendor?.vendor_name || "",
        offered_price: Number(form.offered_price || 0),
        price_mode: "fixed", currency: "EUR",
        delivery_conditions: form.delivery_conditions || null,
        cancellation_policy: form.cancellation_policy || null,
        expiration_date: form.expiration_date || null,
        response_time_hours: form.response_time_hours ? Number(form.response_time_hours) : null,
        language: form.language || null,
        staff_notes: form.staff_notes || null,
        category: req.category,
      };
      await api.post("/admin/offers", payload);
      toast.success(t("adminOffers.toasts.created"));
      setOpenCreate(false);
      setForm({
        request_id: "", vendor_id: "", offered_price: "",
        delivery_conditions: "", cancellation_policy: "", expiration_date: "",
        response_time_hours: "", language: "es", staff_notes: "",
      });
      fetchList();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminOffers.toasts.failed")));
    }
  };

  const runAction = async (action) => {
    if (!openId) return;
    setActing(true);
    try {
      let resp;
      if (action === "submit") resp = await api.post(`/admin/offers/${openId}/submit`, {});
      else if (action === "start_review") resp = await api.post(`/admin/offers/${openId}/start-review`, {});
      else resp = await api.post(`/admin/offers/${openId}/review`, { action, review_reason: reasonInput.trim() || null });
      const updated = resp.data?.offer;
      if (updated) {
        setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        setDetail((p) => p ? { ...p, offer: updated } : p);
      }
      const a = await api.get(`/admin/offers/${openId}/audit-log`);
      setAuditEntries(a.data?.entries || []);
      const tmap = { submit: "submitted", start_review: "reviewing", approve_for_owner: "approved", reject: "rejected", return_to_draft: "returned", cancel: "cancelled" };
      toast.success(t(`adminOffers.toasts.${tmap[action]}`));
      setReasonInput("");
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminOffers.toasts.failed")));
    } finally { setActing(false); }
  };

  const current = detail?.offer?.status;
  const canSubmit = current === "draft";
  const canStartReview = current === "submitted";
  const canApprove = ["submitted", "under_review"].includes(current);
  const canReject = ["submitted", "under_review", "visible_to_owner"].includes(current);
  const canReturn = ["submitted", "under_review", "visible_to_owner"].includes(current);
  const canCancel = ["draft", "submitted", "under_review", "visible_to_owner"].includes(current);
  const reasonRequired = reasonInput.trim().length === 0;

  return (
    <div className="space-y-6" data-testid="admin-offers-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold inline-flex items-center gap-2 text-slate-900">
            <Tag className="w-6 h-6 text-fuchsia-700" />
            {t("adminOffers.pageTitle")}
          </h1>
          <p className="text-sm text-slate-600 mt-1">{t("adminOffers.pageSub")}</p>
          <p className="text-[11px] text-slate-400 inline-flex items-center gap-1 mt-1">
            <Lock className="w-3 h-3" /> {t("adminOffers.privacy")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchList(); fetchPrerequisites(); }}
            data-testid="admin-offers-refresh">
            <RefreshCw className="w-3.5 h-3.5 me-1" /> {t("adminVendors.actions.refresh")}
          </Button>
          <Button onClick={() => setOpenCreate(true)} disabled={requests.length === 0 || vendors.length === 0}
            title={requests.length === 0
              ? t("adminOffers.noRequestApproved")
              : vendors.length === 0
                ? "No active vendors. Create a vendor first."
                : ""}
            data-testid="admin-offers-create">
            <Plus className="w-3.5 h-3.5 me-1" /> {t("adminOffers.create")}
          </Button>
        </div>
      </header>

      {requests.length === 0 && (
        <Card><CardContent className="p-4 text-sm text-amber-800 bg-amber-50">
          {t("adminOffers.noRequestApproved")}
        </CardContent></Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {rows.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="admin-offers-empty">
              {t("adminOffers.empty")}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-offers-table">
              <thead className="bg-slate-50 text-start text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t("adminOffers.fields.vendor")}</th>
                  <th className="px-3 py-2">{t("adminOffers.fields.request")}</th>
                  <th className="px-3 py-2 text-end">{t("adminOffers.fields.price")}</th>
                  <th className="px-3 py-2">{t("adminOffers.fields.expiration")}</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Visibility</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} className="border-t border-slate-100 hover:bg-fuchsia-50/40 cursor-pointer"
                      onClick={() => openDrawer(o)} data-testid={`admin-offers-row-${o.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{o.vendor_name_snapshot || "—"}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs truncate max-w-xs">{o.request_id?.slice(0,8)}…</td>
                    <td className="px-3 py-2 text-end tabular-nums">{fmtEur(o.offered_price)}</td>
                    <td className="px-3 py-2 text-slate-600 tabular-nums">{fmtDate(o.expiration_date)}</td>
                    <td className="px-3 py-2">
                      <Badge className={`${STATUS_PILL[o.status] || "bg-slate-100"} border-0 text-[10px] uppercase`}>
                        {t(`adminOffers.status.${o.status}`)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`text-[10px] ${o.visible_to_owner ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"} border-0`}>
                        {o.visible_to_owner ? t("adminOffers.visibleToOwnerBadge") : t("adminOffers.hiddenFromOwnerBadge")}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Create drawer */}
      <Sheet open={openCreate} onOpenChange={setOpenCreate}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto"
          data-testid="admin-offers-create-drawer">
          <SheetHeader><SheetTitle>{t("adminOffers.create")}</SheetTitle></SheetHeader>
          <div className="space-y-3 mt-4 text-sm">
            <label className="block">
              <span className="text-slate-600 mb-1 block">{t("adminOffers.fields.request")}</span>
              <select value={form.request_id} onChange={(e) => setForm({ ...form, request_id: e.target.value })}
                className="w-full border rounded px-2 py-1.5" data-testid="admin-offers-input-request">
                <option value="">—</option>
                {requests.map((r) => <option key={r.id} value={r.id}>{`${r.title} · ${r.country} · €${r.max_budget || 0}`}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-slate-600 mb-1 block">{t("adminOffers.fields.vendor")}</span>
              <select value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                className="w-full border rounded px-2 py-1.5" data-testid="admin-offers-input-vendor">
                <option value="">—</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{`${v.vendor_name} (${v.country})`}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-slate-600 mb-1 block">{t("adminOffers.fields.price")} (€)</span>
                <Input type="number" min="0" value={form.offered_price}
                  onChange={(e) => setForm({ ...form, offered_price: e.target.value })}
                  data-testid="admin-offers-input-price" />
              </label>
              <label className="block">
                <span className="text-slate-600 mb-1 block">{t("adminOffers.fields.expiration")}</span>
                <Input type="date" value={form.expiration_date}
                  onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
                  data-testid="admin-offers-input-expiration" />
              </label>
              <label className="block">
                <span className="text-slate-600 mb-1 block">{t("adminOffers.fields.responseHours")}</span>
                <Input type="number" min="0" value={form.response_time_hours}
                  onChange={(e) => setForm({ ...form, response_time_hours: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-slate-600 mb-1 block">Language</span>
                <Input value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })} />
              </label>
            </div>
            <label className="block">
              <span className="text-slate-600 mb-1 block">{t("adminOffers.fields.delivery")}</span>
              <Textarea rows={2} value={form.delivery_conditions}
                onChange={(e) => setForm({ ...form, delivery_conditions: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-slate-600 mb-1 block">{t("adminOffers.fields.cancellation")}</span>
              <Textarea rows={2} value={form.cancellation_policy}
                onChange={(e) => setForm({ ...form, cancellation_policy: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-slate-600 mb-1 block">{t("adminOffers.fields.staffNotes")}</span>
              <Textarea rows={3} maxLength={2000} value={form.staff_notes}
                onChange={(e) => setForm({ ...form, staff_notes: e.target.value })}
                data-testid="admin-offers-input-staff-notes" />
            </label>
            <div className="flex gap-2 pt-3 border-t">
              <Button onClick={create} data-testid="admin-offers-save">
                <ShieldCheck className="w-3.5 h-3.5 me-1" /> {t("adminVendors.actions.save")}
              </Button>
              <Button variant="ghost" onClick={() => setOpenCreate(false)} data-testid="admin-offers-create-cancel">
                {t("adminVendors.actions.cancel")}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Detail drawer */}
      <Sheet open={!!openId} onOpenChange={(o) => { if (!o) closeDrawer(); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto"
          data-testid="admin-offers-drawer">
          <SheetHeader>
            <SheetTitle className="inline-flex items-center gap-2">
              {detail?.offer?.vendor_name_snapshot || "—"}
              {current && (
                <Badge className={`${STATUS_PILL[current]} border-0 text-[10px] uppercase`}>
                  {t(`adminOffers.status.${current}`)}
                </Badge>
              )}
            </SheetTitle>
          </SheetHeader>
          {!detail ? (
            <p className="text-sm text-slate-500 mt-4">Loading…</p>
          ) : (
            <div className="space-y-4 mt-4 text-sm">
              <Badge className={`text-[10px] ${detail.offer.visible_to_owner ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
                {detail.offer.visible_to_owner ? t("adminOffers.visibleToOwnerBadge") : t("adminOffers.hiddenFromOwnerBadge")}
              </Badge>

              <section data-testid="admin-offers-source-block">
                <h3 className="font-semibold text-sm mb-2">Request</h3>
                <div className="border rounded p-3 text-xs space-y-1">
                  <div><strong>{detail.request?.title}</strong></div>
                  <div className="flex gap-2 text-slate-600">
                    <span>{detail.request?.country}</span> · <span>{detail.request?.category}</span>
                    · <span>{t("adminOffers.fields.price")}: {fmtEur(detail.offer.offered_price)}</span>
                    · <span>budget: {fmtEur(detail.request?.max_budget)}</span>
                  </div>
                </div>
              </section>

              <section>
                <h3 className="font-semibold text-sm mb-2">Vendor</h3>
                <div className="border rounded p-3 text-xs space-y-1">
                  <div><strong>{detail.vendor?.vendor_name}</strong> · {detail.vendor?.vendor_type} · {detail.vendor?.country}{detail.vendor?.city ? `, ${detail.vendor.city}` : ""}</div>
                  <div className="text-slate-500">verified: {detail.vendor?.verified_status} · active: {detail.vendor?.is_active ? "yes" : "no"}</div>
                </div>
              </section>

              {detail.offer.staff_notes && (
                <section>
                  <h3 className="font-semibold text-sm mb-2">{t("adminOffers.fields.staffNotes")}</h3>
                  <p className="text-xs bg-amber-50 border border-amber-200 rounded p-3 italic"
                     data-testid="admin-offers-staff-notes">{detail.offer.staff_notes}</p>
                </section>
              )}

              <section>
                <label className="block">
                  <span className="text-slate-700 font-medium mb-1 block">{t("adminOffers.reasonLabel")}</span>
                  <Textarea rows={2} maxLength={2000} value={reasonInput}
                    onChange={(e) => setReasonInput(e.target.value)}
                    data-testid="admin-offers-reason-input" />
                </label>
              </section>

              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {canSubmit && (
                  <Button onClick={() => runAction("submit")} disabled={acting} variant="outline"
                    data-testid="admin-offers-action-submit">
                    <Send className="w-3.5 h-3.5 me-1" /> {t("adminOffers.actions.submit")}
                  </Button>
                )}
                {canStartReview && (
                  <Button onClick={() => runAction("start_review")} disabled={acting} variant="outline"
                    data-testid="admin-offers-action-start-review">
                    <RefreshCw className="w-3.5 h-3.5 me-1" /> {t("adminOffers.actions.startReview")}
                  </Button>
                )}
                {canApprove && (
                  <Button onClick={() => runAction("approve_for_owner")} disabled={acting}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="admin-offers-action-approve">
                    <ShieldCheck className="w-3.5 h-3.5 me-1" /> {t("adminOffers.actions.approveForOwner")}
                  </Button>
                )}
                {canReject && (
                  <Button onClick={() => runAction("reject")} disabled={acting || reasonRequired} variant="destructive"
                    data-testid="admin-offers-action-reject">
                    <XCircle className="w-3.5 h-3.5 me-1" /> {t("adminOffers.actions.reject")}
                  </Button>
                )}
                {canReturn && (
                  <Button onClick={() => runAction("return_to_draft")} disabled={acting || reasonRequired} variant="outline"
                    className="border-amber-300 text-amber-900 hover:bg-amber-50"
                    data-testid="admin-offers-action-return">
                    <Undo2 className="w-3.5 h-3.5 me-1" /> {t("adminOffers.actions.returnToDraft")}
                  </Button>
                )}
                {canCancel && (
                  <Button onClick={() => runAction("cancel")} disabled={acting || reasonRequired} variant="ghost"
                    data-testid="admin-offers-action-cancel">
                    <XCircle className="w-3.5 h-3.5 me-1" /> {t("adminOffers.actions.cancel")}
                  </Button>
                )}
                <div className="flex-1" />
                <Button variant="ghost" onClick={closeDrawer} data-testid="admin-offers-action-close">
                  {t("adminOffers.actions.close")}
                </Button>
              </div>

              <section data-testid="admin-offers-audit-log">
                <h3 className="font-semibold text-sm mb-2 inline-flex items-center gap-1">
                  <FlaskConical className="w-3.5 h-3.5 text-fuchsia-700" /> Audit log
                </h3>
                {auditEntries.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">—</p>
                ) : (
                  <ul className="space-y-2 text-xs">
                    {auditEntries.map((e) => (
                      <li key={e.id} className="border-l-2 border-fuchsia-200 ps-3 py-1"
                          data-testid={`admin-offers-audit-row-${e.id}`}>
                        <div className="font-medium text-slate-800">{e.event}</div>
                        <div className="text-slate-500">{e.actor_email || "—"} · {fmtDate(e.at)}</div>
                        {(e.from_status || e.to_status) && (
                          <div className="text-slate-400">{e.from_status || "—"} → {e.to_status || "—"}</div>
                        )}
                        {e.reason && <div className="text-slate-600 bg-slate-50 px-2 py-1 rounded italic mt-1">"{e.reason}"</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminVendorOffers;
